// /src/pages/settings/BillingPage.tsx

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Check, CreditCard, Calendar, AlertCircle, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import PortalLayout from '@/components/portal/PortalLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createCheckoutSession, createBillingPortalSession } from '@/api/stripe';

interface Plan {
  id: string;
  name: string;
  description: string | null;
  price: number;
  features: string[];
  max_chatbots: number;
  stripe_price_id: string;
  is_active: boolean;
}

interface UserProfile {
  id: string;
  subscription_status: string | null;
  plan_id: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  email: string;
}

export default function BillingPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Get current user
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
    };
    getUser();
  }, []);

  // Fetch available plans using direct SQL execution
  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plans' as any)
        .select('*')
        .eq('is_active', true)
        .order('price');
      
      if (error) throw error;
      return (data as any[]).map(plan => ({
        id: plan.id,
        name: plan.name,
        description: plan.description,
        price: parseFloat(plan.price),
        features: plan.features || [],
        max_chatbots: plan.max_chatbots,
        stripe_price_id: plan.stripe_price_id,
        is_active: plan.is_active
      })) as Plan[];
    },
  });

  // Fetch user profile
  const { data: profile, isLoading: profileLoading, refetch: refetchProfile } = useQuery({
    queryKey: ['userProfile', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return null;
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, subscription_status, plan_id, stripe_customer_id, stripe_subscription_id, current_period_start, current_period_end, email')
        .eq('id', currentUser.id)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data as UserProfile | null;
    },
    enabled: !!currentUser?.id,
  });

  // Plan change mutation
  const planChangeMutation = useMutation({
    mutationFn: async (planId: string) => {
      return await createCheckoutSession(planId);
    },
    onSuccess: (data) => {
      toast({
        title: 'Redirecting to Checkout',
        description: 'Opening Stripe checkout...',
      });
      
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to initiate plan change',
        variant: 'destructive',
      });
    },
  });

  // Billing portal mutation
  const billingPortalMutation = useMutation({
    mutationFn: async () => {
      return await createBillingPortalSession();
    },
    onSuccess: (data) => {
      toast({
        title: 'Redirecting to Billing Portal',
        description: 'Opening Stripe billing portal...',
      });
      
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to access billing portal',
        variant: 'destructive',
      });
    },
  });

  const handleSelectPlan = (planId: string) => {
    planChangeMutation.mutate(planId);
  };

  const handleBillingPortal = () => {
    billingPortalMutation.mutate();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'past_due':
        return 'bg-red-100 text-red-800';
      case 'canceled':
        return 'bg-gray-100 text-gray-800';
      case 'free':
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getCurrentPlan = () => {
    if (!profile?.plan_id || !plans) return null;
    return plans.find(plan => plan.id === profile.plan_id);
  };

  const currentPlan = getCurrentPlan();

  return (
    <PortalLayout>
      <div className="container mx-auto py-8 px-4 md:px-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Billing & Subscription</h1>
            <p className="text-gray-600 mt-1">
              Manage your subscription, billing, and payment methods
            </p>
          </div>
        </div>

        {/* Current Subscription Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Current Subscription
            </CardTitle>
            <CardDescription>
              Your current plan details and billing information
            </CardDescription>
          </CardHeader>
          <CardContent>
            {profileLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-[200px]" />
                <Skeleton className="h-4 w-[150px]" />
                <Skeleton className="h-4 w-[180px]" />
              </div>
            ) : profile ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Status:</span>
                    <Badge className={getStatusColor(profile.subscription_status)}>
                      {profile.subscription_status || 'Free'}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-sm font-medium">Current Plan:</span>
                    <p className="text-lg font-semibold text-primary">
                      {currentPlan ? currentPlan.name : 'Free Plan'}
                    </p>
                    {currentPlan && (
                      <p className="text-2xl font-bold">
                        ${currentPlan.price}
                        <span className="text-sm font-normal text-gray-600">/month</span>
                      </p>
                    )}
                  </div>
                </div>
                
                {profile.subscription_status === 'active' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium">Billing Period:</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>Started: {formatDate(profile.current_period_start)}</p>
                      <p>Renews: {formatDate(profile.current_period_end)}</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-600">Unable to load subscription details</p>
            )}

            {/* Billing Portal Access */}
            {profile?.stripe_customer_id && (
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium">Manage Billing</h3>
                    <p className="text-sm text-gray-600">
                      Update payment methods, view invoices, and manage your subscription
                    </p>
                  </div>
                  <Button
                    onClick={handleBillingPortal}
                    disabled={billingPortalMutation.isPending}
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {billingPortalMutation.isPending ? 'Loading...' : 'Billing Portal'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plan Change Alert */}
        {profile?.subscription_status === 'past_due' && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Your subscription payment is past due. Please update your payment method to continue using the service.
            </AlertDescription>
          </Alert>
        )}

        {/* Available Plans */}
        <Card>
          <CardHeader>
            <CardTitle>Available Plans</CardTitle>
            <CardDescription>
              Choose the plan that best fits your needs. You can change your plan at any time.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {plansLoading ? (
              <div className="grid gap-6 md:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="p-6">
                    <Skeleton className="h-6 w-[120px] mb-2" />
                    <Skeleton className="h-8 w-[100px] mb-4" />
                    <div className="space-y-2 mb-6">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-[80%]" />
                      <Skeleton className="h-4 w-[90%]" />
                    </div>
                    <Skeleton className="h-10 w-full" />
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-3">
                {plans?.map((plan) => (
                  <Card
                    key={plan.id}
                    className={`p-6 relative ${
                      plan.id === profile?.plan_id
                        ? 'border-primary ring-2 ring-primary/20'
                        : ''
                    }`}
                  >
                    {plan.id === profile?.plan_id && (
                      <Badge className="absolute -top-2 left-4 bg-primary">
                        Current Plan
                      </Badge>
                    )}
                    
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-xl font-semibold">{plan.name}</h3>
                        <p className="text-3xl font-bold mt-2">
                          ${plan.price}
                          <span className="text-sm font-normal text-gray-600">/month</span>
                        </p>
                      </div>
                      
                      {plan.description && (
                        <p className="text-gray-600 text-sm">{plan.description}</p>
                      )}
                      
                      <ul className="space-y-2">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm">
                            <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <Button
                        variant={plan.id === profile?.plan_id ? "default" : "outline"}
                        className="w-full"
                        onClick={() => handleSelectPlan(plan.id)}
                        disabled={
                          plan.id === profile?.plan_id || planChangeMutation.isPending
                        }
                      >
                        {planChangeMutation.isPending
                          ? 'Processing...'
                          : plan.id === profile?.plan_id
                          ? 'Current Plan'
                          : plan.price > (currentPlan?.price || 0)
                          ? 'Upgrade'
                          : 'Downgrade'}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
